import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  Phone,
  MessageCircle,
  ArrowUpRight,
  Car,
  Bike,
  Truck,
  FileCheck,
  Plus,
  Minus,
  Star,
  Quote,
  LifeBuoy,
  ExternalLink,
} from "lucide-react";
import { useState } from "react";
import { SITE, IMAGES } from "../lib/site";
import { Reveal, MaskedLines } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { ContactMenu } from "../components/ContactMenu";
import { useSeo } from "../hooks/useSeo";

const services = [
  {
    icon: Car,
    n: "01",
    title: "Rottamazione Auto",
    text: "Ritiro e demolizione di autovetture di ogni marca, con o senza documenti, funzionanti o incidentate.",
    span: "md:col-span-7",
  },
  {
    icon: Truck,
    n: "02",
    title: "Furgoni & Commerciali",
    text: "Rottamazione di furgoni, van e veicoli commerciali. Trasporto con carroattrezzi dedicato.",
    span: "md:col-span-5",
  },
  {
    icon: Bike,
    n: "03",
    title: "Moto & Scooter",
    text: "Demolizione di moto, scooter e ciclomotori con radiazione al PRA inclusa.",
    span: "md:col-span-5",
  },
  {
    icon: FileCheck,
    n: "04",
    title: "Pratiche & Certificato PRA",
    text: "Gestiamo noi tutta la burocrazia: certificato di demolizione e radiazione dal Pubblico Registro Automobilistico.",
    span: "md:col-span-7",
  },
];

const chapters = [
  {
    n: "01",
    title: "Ritiro Gratuito",
    text: "Vieni chiamato, fissiamo l'appuntamento e ritiriamo il veicolo direttamente a casa tua, in tutta Roma e provincia. Nessun costo, nessun pensiero.",
  },
  {
    n: "02",
    title: "Pratiche & Documenti",
    text: "Ci occupiamo di ogni adempimento: verifica dei documenti, radiazione al PRA e rilascio del certificato di avvenuta demolizione a norma di legge.",
  },
  {
    n: "03",
    title: "Demolizione Certificata",
    text: "Il veicolo viene avviato a un centro di demolizione autorizzato. Materiali recuperati e smaltiti nel rispetto dell'ambiente.",
  },
];

const zones = [
  "Centro Storico","Prati","Trastevere","EUR","Ostiense","Tuscolano",
  "Appio Latino","Nomentano","Tiburtino","Montesacro","Aurelio","Boccea",
  "Casilino","Prenestino","Ostia","Fiumicino","Guidonia","Pomezia",
];

const reviews = [
  { name: "Marco R.", zone: "Prati", text: "Chiamato la mattina, auto ritirata nel pomeriggio. Gratis e senza pensieri per le pratiche. Servizio serio." },
  { name: "Giulia T.", zone: "EUR", text: "Avevo una macchina ferma da anni nel box. L'hanno portata via loro e mi hanno consegnato il certificato di demolizione. Perfetti." },
  { name: "Alessandro D.", zone: "Tuscolano", text: "Furgone incidentato senza documenti completi: mi hanno spiegato tutto e risolto in due giorni. Consigliatissimi." },
  { name: "Francesca M.", zone: "Ostia", text: "Gentili e puntuali. Radiazione al PRA gestita da loro, io non ho fatto una fila. Zero costi come promesso." },
];

const faqs = [
  { q: "Il ritiro del veicolo è davvero gratuito?", a: "Sì. Il ritiro a domicilio in Roma e provincia è completamente gratuito, senza costi nascosti." },
  { q: "Posso rottamare un'auto senza documenti?", a: "In molti casi sì. Contattaci e valutiamo insieme la situazione: spesso è sufficiente una denuncia di smarrimento." },
  { q: "Quanto tempo serve per la rottamazione?", a: "Di norma il ritiro avviene entro 24-48 ore dalla chiamata. Le pratiche di radiazione al PRA le gestiamo interamente noi." },
  { q: "Rilasciate il certificato di demolizione?", a: "Certo. A fine procedura ricevi il certificato di avvenuta demolizione e la radiazione ufficiale dal PRA." },
];

const Faq = ({ item, i }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[#232323]">
      <button
        data-testid={`faq-${i}`}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-6 py-6 text-left transition-colors duration-150 hover:text-[#ff3300]"
      >
        <span className="font-display text-2xl md:text-3xl tracking-wide">
          {item.q}
        </span>
        <span className="shrink-0">
          {open ? <Minus size={22} /> : <Plus size={22} />}
        </span>
      </button>
      <motion.div
        initial={false}
        animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        className="overflow-hidden"
      >
        <p className="max-w-2xl pb-7 font-mono text-sm leading-relaxed text-[#8a8a8a]">
          {item.a}
        </p>
      </motion.div>
    </div>
  );
};

export default function Home() {
  useSeo({
    title: "Rottamazione Auto Roma | Ritiro Gratuito & Demolizione H24",
    description:
      "Rottamazione auto a Roma e provincia: ritiro gratuito a domicilio, demolizione certificata e radiazione al PRA. Attivi h24. Chiama o scrivi su WhatsApp.",
    path: "/",
  });
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const yImg = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);
  const scaleImg = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <div>
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[100svh] overflow-hidden">
        <motion.div
          style={{ y: yImg, scale: scaleImg }}
          className="absolute inset-0"
        >
          <img
            src={IMAGES.heroCrushed}
            alt="Cumulo di auto rottamate"
            className="h-full w-full object-cover opacity-40 img-grayscale"
            style={{ filter: "grayscale(1) contrast(1.1)" }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/40 to-[#050505]/70" />
        </motion.div>

        <div className="relative z-10 mx-auto flex min-h-[100svh] max-w-[1400px] flex-col justify-end px-5 md:px-8 pb-16 pt-32">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="mb-6 flex items-center gap-3 font-mono text-xs uppercase tracking-[0.2em] text-[#8a8a8a]"
          >
            <span className="h-2 w-2 animate-pulse bg-[#ff3300]" />
            Attivi {SITE.hours}
          </motion.p>

          <h1 className="font-num uppercase leading-[0.82] tracking-tight text-[15vw] md:text-[11.5vw]">
            <MaskedLines
              lines={["Rottamazione", "Auto a Roma"]}
              delay={0.25}
            />
          </h1>

          <div className="mt-8 flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
            <Reveal delay={0.9} className="max-w-md">
              <p className="font-mono text-sm leading-relaxed text-[#8a8a8a]">
                Ritiro gratuito a domicilio, demolizione certificata e
                radiazione al PRA. Ci pensiamo noi a tutto — tu fai solo una
                chiamata.
              </p>
            </Reveal>

            <Reveal delay={1} className="flex flex-wrap gap-3">
              <ContactMenu
                type="call"
                label="Chiama Ora"
                testid="hero-call"
                className="group flex items-center gap-3 border border-[#ff3300] bg-[#ff3300] px-6 py-4 font-mono text-xs uppercase tracking-[0.16em] text-black transition-colors duration-150 hover:bg-transparent hover:text-[#ff3300]"
              />
              <ContactMenu
                type="whatsapp"
                label="WhatsApp"
                testid="hero-whatsapp"
                className="group flex items-center gap-3 border border-[#232323] bg-transparent px-6 py-4 font-mono text-xs uppercase tracking-[0.16em] text-[#f4f4f0] transition-colors duration-150 hover:border-[#25d366] hover:text-[#25d366]"
              />
            </Reveal>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <Marquee
        items={["Ritiro Gratuito", "Demolizione H24", "Certificato PRA", "Roma & Provincia"]}
      />

      {/* PROCESS MANIFESTO */}
      <section className="mx-auto max-w-[1400px] px-5 md:px-8 py-24 md:py-36">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#8a8a8a]">
            / Come funziona
          </p>
          <h2 className="mt-4 max-w-3xl font-display text-5xl md:text-7xl leading-[0.95] tracking-wide">
            Tre passi. Zero pensieri. Nessun costo di ritiro.
          </h2>
        </Reveal>

        <div className="mt-16 flex flex-col">
          {chapters.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.05}>
              <div className="group grid grid-cols-1 items-start gap-4 border-t border-[#232323] py-10 md:grid-cols-12 md:gap-8">
                <span className="font-num text-7xl md:text-8xl leading-none text-outline-red md:col-span-3">
                  {c.n}
                </span>
                <h3 className="font-display text-4xl md:text-5xl tracking-wide md:col-span-5">
                  {c.title}
                </h3>
                <p className="font-mono text-sm leading-relaxed text-[#8a8a8a] md:col-span-4">
                  {c.text}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section className="border-t border-[#232323] bg-[#050505] py-24 md:py-36">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8">
          <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
            <Reveal>
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#8a8a8a]">
                / Servizi
              </p>
              <h2 className="mt-4 font-display text-5xl md:text-7xl tracking-wide">
                Cosa rottamiamo
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <Link
                to="/servizi"
                data-testid="services-link"
                className="group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.16em] text-[#8a8a8a] transition-colors duration-150 hover:text-[#ff3300]"
              >
                Tutti i servizi
                <ArrowUpRight size={16} className="transition-transform duration-200 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </Link>
            </Reveal>
          </div>

          <div className="mt-14 grid grid-cols-1 gap-4 md:grid-cols-12">
            {services.map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal key={s.n} delay={i * 0.06} className={s.span}>
                  <div className="group flex h-full flex-col justify-between border border-[#232323] bg-[#0f0f0f] p-8 transition-colors duration-200 hover:bg-[#1a1a1a]">
                    <div className="flex items-start justify-between">
                      <Icon size={30} strokeWidth={1.5} className="text-[#ff3300]" />
                      <span className="font-num text-3xl leading-none text-[#232323]">
                        {s.n}
                      </span>
                    </div>
                    <div className="mt-16">
                      <h3 className="font-display text-3xl md:text-4xl tracking-wide">
                        {s.title}
                      </h3>
                      <p className="mt-3 max-w-md font-mono text-sm leading-relaxed text-[#8a8a8a]">
                        {s.text}
                      </p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* IMAGE SPLIT / SPOTLIGHT */}
      <section className="mx-auto grid max-w-[1400px] grid-cols-1 items-center gap-10 px-5 md:px-8 py-24 md:py-36 md:grid-cols-2">
        <Reveal className="group relative aspect-[4/5] overflow-hidden border border-[#232323]">
          <img
            src={IMAGES.towTruck}
            alt="Carroattrezzi che carica un veicolo"
            className="h-full w-full object-cover img-grayscale"
          />
          <span className="absolute left-4 top-4 bg-[#ff3300] px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-black">
            Ritiro a domicilio
          </span>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#8a8a8a]">
            / Perché noi
          </p>
          <h2 className="mt-4 font-display text-5xl md:text-6xl leading-[0.95] tracking-wide">
            Un veicolo da eliminare? Lo trasformiamo in un problema risolto.
          </h2>
          <div className="mt-10 grid grid-cols-2 gap-px border border-[#232323] bg-[#232323]">
            {[
              ["100%", "Ritiro gratuito"],
              ["24/7", "Sempre attivi"],
              ["PRA", "Radiazione inclusa"],
              ["24-48h", "Tempi di ritiro"],
            ].map(([big, small]) => (
              <div key={small} className="bg-[#0f0f0f] p-6">
                <p className="font-num text-4xl leading-none text-[#f4f4f0]">
                  {big}
                </p>
                <p className="mt-2 font-mono text-xs uppercase tracking-[0.14em] text-[#8a8a8a]">
                  {small}
                </p>
              </div>
            ))}
          </div>
        </Reveal>
      </section>

      {/* REVIEWS */}
      <section className="border-t border-[#232323] bg-[#0f0f0f] py-24 md:py-36">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8">
          <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
            <Reveal>
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#8a8a8a]">
                / Recensioni
              </p>
              <h2 className="mt-4 font-display text-5xl md:text-7xl tracking-wide">
                Dicono di noi
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="flex items-center gap-3">
                <div className="flex gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={18} className="fill-[#ffd600] text-[#ffd600]" />
                  ))}
                </div>
                <span className="font-mono text-xs uppercase tracking-[0.14em] text-[#8a8a8a]">
                  Clienti soddisfatti a Roma
                </span>
              </div>
            </Reveal>
          </div>

          <div className="mt-14 grid grid-cols-1 gap-4 md:grid-cols-2">
            {reviews.map((r, i) => (
              <Reveal key={r.name} delay={(i % 2) * 0.08}>
                <div className="group flex h-full flex-col justify-between border border-[#232323] bg-[#050505] p-8 transition-colors duration-200 hover:bg-[#1a1a1a]">
                  <div className="flex items-start justify-between">
                    <div className="flex gap-1">
                      {[...Array(5)].map((_, s) => (
                        <Star key={s} size={14} className="fill-[#ff3300] text-[#ff3300]" />
                      ))}
                    </div>
                    <Quote size={28} className="text-[#232323]" />
                  </div>
                  <p className="mt-8 font-mono text-base leading-relaxed text-[#f4f4f0]">
                    “{r.text}”
                  </p>
                  <div className="mt-8 flex items-center gap-3 border-t border-[#232323] pt-5">
                    <span className="flex h-9 w-9 items-center justify-center bg-[#ff3300] font-num text-sm leading-none text-black">
                      {r.name.charAt(0)}
                    </span>
                    <div>
                      <p className="font-display text-lg tracking-wide leading-none">{r.name}</p>
                      <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.16em] text-[#8a8a8a]">
                        {r.zone} · Roma
                      </p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section className="border-t border-[#232323] bg-[#050505] py-24 md:py-36">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#8a8a8a]">
              / Galleria
            </p>
            <h2 className="mt-4 font-display text-5xl md:text-7xl tracking-wide">
              I nostri mezzi al lavoro
            </h2>
            <p className="mt-4 max-w-xl font-mono text-sm text-[#8a8a8a]">
              Carroattrezzi a pianale per il ritiro di ogni veicolo, in tutta
              Roma e provincia.
            </p>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {IMAGES.gallery.map((g, i) => (
              <Reveal key={i} delay={(i % 4) * 0.06}>
                <div className="group relative aspect-[4/5] overflow-hidden border border-[#232323]">
                  <img
                    src={g.url}
                    alt={`Rottamazione Auto Roma — ${g.label}`}
                    loading="lazy"
                    className="h-full w-full object-cover img-grayscale"
                  />
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#f4f4f0]">
                      {String(i + 1).padStart(2, "0")} — {g.label}
                    </span>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ZONES STRIP */}
      <section className="border-t border-[#232323] bg-[#050505] py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8">
          <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
            <Reveal>
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#8a8a8a]">
                / Copertura
              </p>
              <h2 className="mt-4 font-display text-5xl md:text-7xl tracking-wide">
                Tutta Roma
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <Link
                to="/zone"
                className="group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.16em] text-[#8a8a8a] transition-colors duration-150 hover:text-[#ff3300]"
              >
                Tutte le zone
                <ArrowUpRight size={16} className="transition-transform duration-200 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </Link>
            </Reveal>
          </div>
          <div className="mt-12 flex flex-wrap gap-x-2 gap-y-3">
            {zones.map((z) => (
              <span
                key={z}
                className="border border-[#232323] px-4 py-2 font-mono text-xs uppercase tracking-[0.1em] text-[#8a8a8a] transition-colors duration-150 hover:border-[#ff3300] hover:text-[#f4f4f0]"
              >
                {z}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* SOCCORSO STRADALE */}
      <section className="border-t border-[#232323] bg-[#0f0f0f] py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8">
          <div className="grid grid-cols-1 items-center gap-10 border border-[#232323] bg-[#050505] p-8 md:p-14 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <p className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.2em] text-[#ff3300]">
                <LifeBuoy size={16} /> Soccorso Stradale
              </p>
              <h2 className="mt-5 font-display text-5xl md:text-7xl leading-[0.95] tracking-wide">
                Hai bisogno di soccorso stradale?
              </h2>
              <p className="mt-6 max-w-2xl font-mono text-sm leading-relaxed text-[#8a8a8a]">
                Oltre alla rottamazione, offriamo anche il{" "}
                <span className="text-[#f4f4f0]">recupero auto 24/7</span>: auto
                in panne, incidentate, con guasto o bloccate in strada. Il
                nostro carroattrezzi interviene in tutta Roma e provincia, a
                qualsiasi ora del giorno e della notte.
              </p>
            </div>
            <div className="lg:col-span-4 lg:justify-self-end">
              <a
                href={SITE.soccorso}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="soccorso-cta"
                className="group inline-flex items-center gap-3 border border-[#ff3300] bg-[#ff3300] px-7 py-5 font-mono text-xs uppercase tracking-[0.16em] text-black transition-colors duration-150 hover:bg-transparent hover:text-[#ff3300]"
              >
                Vai a Carroattrezzi Roma H24
                <ExternalLink
                  size={16}
                  className="transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-[1400px] px-5 md:px-8 py-24 md:py-36">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#8a8a8a]">
            / Domande frequenti
          </p>
          <h2 className="mt-4 font-display text-5xl md:text-7xl tracking-wide">
            Le risposte
          </h2>
        </Reveal>
        <div className="mt-12 border-t border-[#232323]">
          {faqs.map((f, i) => (
            <Faq key={i} item={f} i={i} />
          ))}
        </div>
      </section>
    </div>
  );
}
